// A popup for a new message (or a task given to you) while this tab is the one being looked at — the other half of DesktopNotifications,
// which only fires while the tab is in the background. Same events, same rules: mentions, thread replies and direct
// messages, nothing while Do not disturb is on, and nothing for the conversation already on screen. Clicking one
// opens the message.
import { useEffect, useState } from "react";
import { useLocation, useMatch, useNavigate } from "react-router";
import { NOTIFICATION_LABEL as LABEL, notificationHref } from "../../lib/chat";
import { useLiveEvent } from "../../lib/live";
import { useMe } from "../../lib/session";
import { isDnd } from "../../lib/people";
import { Avatar } from "../../components/ui";
import { Icon } from "../../components/icons";

const SHOWN_FOR = 7000;
const AT_ONCE = 3;

type Popup = { id: string; kind: keyof typeof LABEL; conversationId: string | null; parentId: string | null; taskId?: string | null; projectId?: string | null; actor: { id: string; name: string }; preview: string };

// A short blip, quiet enough to live with. Sound is blocked until the first click on the page, and a browser may
// refuse one for its own reasons; either way a popup without a sound is still a popup, so nothing here may throw.
function blip() {
  try {
    const ctx = new AudioContext();
    if (ctx.state === "suspended") { void ctx.close().catch(() => {}); return; }
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    const o = ctx.createOscillator();
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.setValueAtTime(1180, ctx.currentTime + 0.08);
    o.connect(gain);
    gain.gain.setValueAtTime(0.05, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.22);
    o.start();
    o.stop(ctx.currentTime + 0.24);
    o.onended = () => { void ctx.close().catch(() => {}); };
  } catch { /* no sound here */ }
}

export function MessagePopups({ slug }: { slug: string }) {
  const navigate = useNavigate();
  const me = useMe().data;
  const onScreen = useMatch("/o/:slug/chat/:conversationId")?.params.conversationId ?? null;
  const taskOnScreen = new URLSearchParams(useLocation().search).get("task");
  const [popups, setPopups] = useState<Popup[]>([]);
  const dismiss = (id: string) => setPopups((list) => list.filter((p) => p.id !== id));

  useLiveEvent((e) => {
    if (e.type !== "notification.created" || document.visibilityState !== "visible") return;
    if (isDnd(me?.user.dndUntil)) return;
    const n = e.notification as Popup;
    if (!n?.id || (n.conversationId && n.conversationId === onScreen) || (n.taskId && n.taskId === taskOnScreen)) return;
    setPopups((list) => [...list.filter((p) => p.id !== n.id), n].slice(-AT_ONCE));
    blip();
  });

  // Each one goes away on its own; a popup nobody clicked should not sit there all afternoon.
  useEffect(() => {
    const timers = popups.map((p) => setTimeout(() => dismiss(p.id), SHOWN_FOR));
    return () => timers.forEach(clearTimeout);
  }, [popups]);

  if (!popups.length) return null;

  return (
    <div className="pointer-events-none fixed right-4 top-4 z-40 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2" role="region" aria-label="New messages">
      {popups.map((p) => (
        <div key={p.id} role="status" className="pointer-events-auto flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3 text-left shadow-xl dark:border-slate-700 dark:bg-slate-800">
          <button
            type="button"
            onClick={() => { dismiss(p.id); navigate(notificationHref(slug, p)); }}
            className="flex min-w-0 flex-1 items-start gap-3 text-left"
          >
            <Avatar name={p.actor.name} userId={p.actor.id} />
            <span className="min-w-0 flex-1">
              <span className="line-clamp-2 block text-sm font-semibold text-slate-900 dark:text-slate-100">{p.actor.name} <span className="font-normal text-slate-500 dark:text-slate-400">{LABEL[p.kind] ?? ""}</span></span>
              {p.preview && <span className="mt-0.5 block line-clamp-2 text-sm text-slate-600 dark:text-slate-300">{p.preview}</span>}
            </span>
          </button>
          <button type="button" onClick={() => dismiss(p.id)} aria-label="Dismiss" className="shrink-0 rounded-md p-1.5 [@media(pointer:coarse)]:p-2.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700">
            <Icon name="x" className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
